/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// "Create an account" — Figma file gtBsWqJJcI6ZBuBv51RU35 node
// 3281:98433. Tab-switched single-field signup. Password / Confirm
// Password are intentionally not rendered: the backend swagger is
// OTP-only and the signup flow forwards the email/phone straight to
// /v1/auth/{email|sms}-request.

import { useState } from 'react';
import { View } from 'react-native';
import {
	AuthCrossLinkFooter,
	AuthField,
	AuthHeader,
	AuthInput,
	AuthLayout,
	AuthPrimaryButton,
} from '../AuthLayout';
import { AuthTabs } from '../AuthTabs';
import { validateEmail, validatePhone } from '../authValidators';

type Tab = 'phone' | 'email';

export interface ChooseMethodScreenProps {
	readonly onSubmit: (contact: { kind: 'email'; value: string } | { kind: 'phone'; value: string }) => void;
	readonly onChooseExisting: () => void;
	readonly externalError?: string | null;
	readonly isSubmitting?: boolean;
}

export function ChooseMethodScreen({
	onSubmit,
	onChooseExisting,
	externalError,
	isSubmitting,
}: ChooseMethodScreenProps): JSX.Element {
	const [tab, setTab] = useState<Tab>('email');
	const [phone, setPhone] = useState('');
	const [email, setEmail] = useState('');
	const [error, setError] = useState<string | null>(null);

	const fieldError = externalError ?? error;
	const isEmailTab = tab === 'email';
	const contactValue = isEmailTab ? email : phone;

	const handleSubmit = (): void => {
		if (isEmailTab) {
			const result = validateEmail(email);
			if (!result.ok) {
				setError(result.reason === 'empty' ? 'Email required' : 'Email is not valid');
				return;
			}
			setError(null);
			onSubmit({ kind: 'email', value: result.value });
			return;
		}
		const result = validatePhone(phone);
		if (!result.ok) {
			setError(phoneErrorCopy(result.reason));
			return;
		}
		setError(null);
		onSubmit({ kind: 'phone', value: result.value });
	};

	const canSubmit = contactValue.trim().length > 0 && !isSubmitting;

	return (
		<AuthLayout accessibilityLabel="chooseMethod">
			<AuthHeader title="Create an account" />

			<View style={{ flexDirection: 'column' }}>
				<AuthTabs<Tab>
					value={tab}
					options={[
						{ value: 'phone', label: 'Phone' },
						{ value: 'email', label: 'Email' },
					]}
					onChange={(next) => {
						setTab(next);
						if (error !== null) setError(null);
					}}
				/>

				<View style={{ height: 32 }} />

				<AuthField label={isEmailTab ? 'Email' : 'Phone number'} error={fieldError}>
					<AuthInput
						value={contactValue}
						onChangeText={(next) => {
							if (isEmailTab) setEmail(next);
							else setPhone(next);
							if (error !== null) setError(null);
						}}
						placeholder={isEmailTab ? 'Enter your email' : 'Enter your phone number'}
						autoCapitalize="none"
						keyboardType={isEmailTab ? 'email-address' : 'phone-pad'}
						textContentType={isEmailTab ? 'emailAddress' : 'telephoneNumber'}
						hasError={Boolean(fieldError)}
						accessibilityLabel={`ping_auth_signup_${tab}`}
					/>
				</AuthField>

				<View style={{ height: 32 }} />

				<AuthPrimaryButton
					onPress={handleSubmit}
					disabled={!canSubmit}
					accessibilityLabel="ping_auth_signup_continue"
				>
					{isSubmitting ? 'Sending code…' : 'Continue'}
				</AuthPrimaryButton>
			</View>

			<AuthCrossLinkFooter
				prompt="Already have an account?"
				actionLabel="Log in"
				onPressAction={onChooseExisting}
			/>
		</AuthLayout>
	);
}

function phoneErrorCopy(
	reason: 'empty' | 'invalidCountryCode' | 'tooShort' | 'tooLong' | 'invalidFormat',
): string {
	switch (reason) {
		case 'empty':
			return 'Phone number required';
		case 'invalidCountryCode':
			return 'Include a country code (e.g. +1)';
		case 'tooShort':
			return 'Phone number is too short';
		case 'tooLong':
			return 'Phone number is too long';
		case 'invalidFormat':
			return 'Phone number is not valid';
	}
}
